/**
 * MonetaryEngine
 * Highly precise arithmetic engine for NUMERIC(10,4) operations.
 * Uses scaled integers (scale = 10000) to avoid JavaScript floating-point inaccuracies.
 * Implements banker's rounding (ROUND_HALF_EVEN).
 */
export class MonetaryEngine {
  private static readonly SCALE = 10000;

  /**
   * Converts a number or string representation of a decimal into a scaled integer.
   */
  public static toScaledInt(value: number | string): bigint {
    const valStr = typeof value === "number" ? value.toFixed(8) : value;
    const parts = valStr.split(".");
    const integerPart = parts[0];
    const decimalPart = (parts[1] || "").slice(0, 8).padEnd(8, "0");

    // We want scale 10,000 (4 decimals).
    // Let's get 4 decimal digits and look at the remaining to round properly.
    const fourDecimals = decimalPart.slice(0, 4);
    const fractionPart = decimalPart.slice(4);

    let scaled = BigInt(integerPart + fourDecimals);
    const fraction = parseInt(fractionPart, 10); // 4 digits left

    // Fraction is out of 10,000. Halfway is exactly 5000.
    if (fraction > 5000) {
      scaled += 1n;
    } else if (fraction === 5000) {
      // Banker's rounding: round to nearest even
      if (scaled % 2n !== 0n) {
        scaled += 1n;
      }
    }
    // If fraction < 5000, do nothing (round down)
    return scaled;
  }

  /**
   * Converts a scaled integer back into a number.
   */
  public static toDouble(scaled: bigint): number {
    return Number(scaled) / this.SCALE;
  }

  /**
   * Helper to format double to string with exactly 4 decimal places.
   */
  public static format(value: number | bigint): string {
    if (typeof value === "bigint") {
      const val = this.toDouble(value);
      return val.toFixed(4);
    }
    return value.toFixed(4);
  }

  /**
   * Banker's rounding of a regular JavaScript number to 4 decimal places.
   */
  public static roundHalfEven(value: number): number {
    return this.toDouble(this.toScaledInt(value));
  }

  /**
   * Safely adds two decimal numbers.
   */
  public static add(a: number | string, b: number | string): number {
    const scaledA = this.toScaledInt(a);
    const scaledB = this.toScaledInt(b);
    return this.toDouble(scaledA + scaledB);
  }

  /**
   * Safely subtracts b from a.
   */
  public static subtract(a: number | string, b: number | string): number {
    const scaledA = this.toScaledInt(a);
    const scaledB = this.toScaledInt(b);
    return this.toDouble(scaledA - scaledB);
  }

  /**
   * Safely multiplies two decimal numbers.
   * Multiplication: (A * SCALE) * (B * SCALE) = A * B * SCALE^2.
   * To keep SCALE, we must divide the result by SCALE and do banker's rounding.
   */
  public static multiply(a: number | string, b: number | string): number {
    const scaledA = this.toScaledInt(a);
    const scaledB = this.toScaledInt(b);
    const resultBig = scaledA * scaledB;

    // We need to divide by SCALE (10000) and perform banker's rounding.
    const div = resultBig / 10000n;
    const rem = resultBig % 10000n;
    let finalScaled = div;

    // Scale is 10000, halfway is 5000.
    // Handle signs correctly
    const absRem = rem < 0n ? -rem : rem;
    if (absRem > 5000n) {
      finalScaled += resultBig < 0n ? -1n : 1n;
    } else if (absRem === 5000n) {
      if (finalScaled % 2n !== 0n) {
        finalScaled += resultBig < 0n ? -1n : 1n;
      }
    }
    return this.toDouble(finalScaled);
  }

  /**
   * Safely divides a by b.
   * Division: (A * SCALE) / (B * SCALE) = A / B.
   * To keep SCALE, we scale A by SCALE first: (A * SCALE^2) / (B * SCALE) = (A / B) * SCALE.
   */
  public static divide(a: number | string, b: number | string): number {
    const scaledA = this.toScaledInt(a);
    const scaledB = this.toScaledInt(b);

    if (scaledB === 0n) {
      throw new Error("Divisão por zero no motor monetário.");
    }

    const scaledNum = scaledA * 10000n;
    const div = scaledNum / scaledB;
    const rem = scaledNum % scaledB;
    let finalScaled = div;

    // Halfway checks
    const absRem = rem < 0n ? -rem : rem;
    const absB = scaledB < 0n ? -scaledB : scaledB;
    const half = absB / 2n;
    const isExactHalf = absB % 2n === 0n && absRem === half;

    if (isExactHalf) {
      if (finalScaled % 2n !== 0n) {
        finalScaled += (scaledNum < 0n !== scaledB < 0n) ? -1n : 1n;
      }
    } else if (absRem * 2n > absB) {
      finalScaled += (scaledNum < 0n !== scaledB < 0n) ? -1n : 1n;
    }

    return this.toDouble(finalScaled);
  }

  /**
   * Compares two numbers. Returns:
   * - 1 if a > b
   * - -1 if a < b
   * - 0 if a === b (under 4 decimal precision)
   */
  public static compare(a: number | string, b: number | string): number {
    const scaledA = this.toScaledInt(a);
    const scaledB = this.toScaledInt(b);
    if (scaledA > scaledB) return 1;
    if (scaledA < scaledB) return -1;
    return 0;
  }
}
